import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import { useAuthStore } from '../../store/authStore';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { Calendar, Clock, MapPin, User, Phone, QrCode, Check, X, Download } from 'lucide-react';

const STATUS_LABELS = {
  pending: { label: 'بانتظار التأكيد', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
  confirmed: { label: 'مؤكد', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30' },
  active: { label: 'جارٍ التنفيذ', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' },
  completed: { label: 'مكتمل', color: 'text-slate-300', bg: 'bg-slate-500/10 border-slate-500/30' },
  cancelled: { label: 'ملغي', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30' },
};

export default function BookingDetailPage() {
  const { uuid } = useParams();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(['booking', uuid], () =>
    api.get(`/bookings/${uuid}`).then(r => r.data)
  );

  const updateStatus = useMutation(
    ({ status }) => api.put(`/bookings/${uuid}/status`, { status }),
    { onSuccess: () => queryClient.invalidateQueries(['booking', uuid]) }
  );

  if (isLoading) return (
    <div className="space-y-4">
      {[1,2,3].map(i => <div key={i} className="skeleton h-32 rounded-2xl" />)}
    </div>
  );

  const b = data?.booking;
  if (!b) return <div className="text-slate-400 text-center py-12">الحجز غير موجود</div>;

  const statusInfo = STATUS_LABELS[b.status] || STATUS_LABELS.pending;
  const isOwner = user?.id === b.owner_id;

  return (
    <div className="max-w-2xl mx-auto space-y-5" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">تفاصيل الحجز</h1>
        <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${statusInfo.bg} ${statusInfo.color}`}>
          {statusInfo.label}
        </span>
      </div>

      {/* Booking Reference */}
      <div className="glass rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-sm mb-1">رقم الحجز</p>
            <p className="text-white font-bold text-xl font-mono">{b.booking_reference}</p>
          </div>
          {b.qr_code && (
            <div className="bg-white p-2 rounded-xl">
              <img src={b.qr_code} alt="QR Code" className="w-20 h-20" />
            </div>
          )}
        </div>
      </div>

      {/* Listing Info */}
      <div className="glass rounded-2xl p-5">
        <h3 className="font-bold text-white mb-3">{b.listing_title}</h3>
        <div className="space-y-2 text-sm text-slate-400">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-primary-400" />
            <span>{format(new Date(b.start_time), 'EEEE dd MMMM yyyy', { locale: ar })}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-primary-400" />
            <span>{format(new Date(b.start_time), 'HH:mm')} — {format(new Date(b.end_time), 'HH:mm')} ({b.total_hours} ساعة)</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-primary-400" />
            <span>{b.listing_city}</span>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="glass rounded-2xl p-5">
        <h3 className="font-bold text-white mb-4">ملخص التكلفة</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-slate-400">
            <span>{b.price_per_hour} × {b.total_hours} ساعة</span>
            <span>{b.subtotal} {b.currency}</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span>عمولة المنصة (10%)</span>
            <span>{b.platform_commission} {b.currency}</span>
          </div>
          <div className="border-t border-slate-700 pt-2 flex justify-between text-white font-bold text-base">
            <span>الإجمالي</span>
            <span className="text-primary-400">{b.total_amount} {b.currency}</span>
          </div>
        </div>
      </div>

      {/* Parties */}
      <div className="grid grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-5">
          <p className="text-slate-400 text-xs mb-2">المستأجر</p>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center">
              <User size={14} className="text-primary-400" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">{b.renter_name}</p>
              <p className="text-slate-500 text-xs">{b.renter_phone}</p>
            </div>
          </div>
        </div>
        <div className="glass rounded-2xl p-5">
          <p className="text-slate-400 text-xs mb-2">المالك</p>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
              <User size={14} className="text-green-400" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">{b.owner_name}</p>
              <p className="text-slate-500 text-xs">{b.owner_phone}</p>
            </div>
          </div>
        </div>
      </div>

      {b.special_requests && (
        <div className="glass rounded-2xl p-5">
          <h3 className="font-bold text-white mb-2 text-sm">الملاحظات الخاصة</h3>
          <p className="text-slate-400 text-sm">{b.special_requests}</p>
        </div>
      )}

      {/* Actions for Owner */}
      {isOwner && b.status === 'pending' && (
        <div className="flex gap-3">
          <button
            onClick={() => updateStatus.mutate({ status: 'confirmed' })}
            disabled={updateStatus.isLoading}
            className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-medium transition-colors"
          >
            <Check size={18} /> تأكيد الحجز
          </button>
          <button
            onClick={() => updateStatus.mutate({ status: 'cancelled' })}
            disabled={updateStatus.isLoading}
            className="flex-1 flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 py-3 rounded-xl font-medium transition-colors"
          >
            <X size={18} /> رفض الحجز
          </button>
        </div>
      )}

      {/* Cancel for Renter */}
      {!isOwner && ['pending', 'confirmed'].includes(b.status) && (
        <button
          onClick={() => updateStatus.mutate({ status: 'cancelled' })}
          disabled={updateStatus.isLoading}
          className="w-full flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 py-3 rounded-xl font-medium transition-colors"
        >
          <X size={18} /> إلغاء الحجز
        </button>
      )}
    </div>
  );
}
